import { useEffect, useState, useCallback, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, RefreshCcw, FileText, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { SUBSIDY_CATEGORIES } from "@/lib/subsidies";

const API = `/api`;

function toCSV(rows) {
  if (!rows.length) return "";
  const cols = ["id", "category", "name", "phone", "location", "company", "email", "notes", "documents_count", "created_at"];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => {
    if (c === "documents_count") return escape((r.documents || []).length);
    return escape(r[c]);
  }).join(","))].join("\n");
}
function saveCSV(name, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function SubsidyAdmin({ password }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("ALL");

  const fetchAll = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subsidy-submissions`, {
        headers: { "X-Admin-Password": password },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load subsidy submissions");
    } finally { setLoading(false); }
  }, [password]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = filter === "ALL" ? rows : rows.filter((r) => r.category === filter);

  const downloadFile = async (storagePath, filename) => {
    try {
      const resp = await axios.get(`${API}/admin/subsidy-files/${storagePath}`, {
        headers: { "X-Admin-Password": password },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const exportCSV = () => {
    if (!filtered.length) return toast.error("No data to export");
    saveCSV(`biomass-zone-subsidy-${filter.toLowerCase()}.csv`, toCSV(filtered));
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Govt. Subsidy</div>
          <h2 className="font-display text-2xl mt-1 text-slate-900">Subsidy submissions</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAll} variant="ghost" size="sm" disabled={loading}
            data-testid="subsidy-refresh-btn" className="text-slate-600 hover:text-emerald-700">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={exportCSV} data-testid="subsidy-export-csv"
            className="bz-btn-primary rounded-full text-white">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="subsidy-filter-tabs">
        <button onClick={() => setFilter("ALL")}
          data-testid="subsidy-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === "ALL"
            ? "bg-emerald-600 text-white border-emerald-600"
            : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500"}`}>
          All <span className="opacity-70 ml-1">{rows.length}</span>
        </button>
        {SUBSIDY_CATEGORIES.map((c) => {
          const count = rows.filter((r) => r.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)}
              data-testid={`subsidy-filter-${c.key.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === c.key
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500"}`}>
              {c.code} <span className="opacity-70 ml-1">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="bz-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !filtered.length ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="subsidy-empty">
            No subsidy submissions yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>Category</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Docs</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const isOpen = !!expanded[r.id];
                  return (
                    <Fragment key={r.id}>
                      <TableRow
                        data-testid={`subsidy-row-${r.id}`}
                        className="border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                        onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}>
                        <TableCell>
                          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        </TableCell>
                        <TableCell>
                          <span className="text-[10px] tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                            {r.category}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{r.phone}</TableCell>
                        <TableCell className="text-slate-600">{r.company || "—"}</TableCell>
                        <TableCell className="text-slate-600">{r.location}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                            <FileText className="w-3.5 h-3.5" /> {(r.documents || []).length}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs">
                          {new Date(r.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow key={`${r.id}-detail`} className="bg-slate-50/60">
                          <TableCell />
                          <TableCell colSpan={7}>
                            <div className="py-2 space-y-3">
                              {r.email && (
                                <div className="text-xs text-slate-600">
                                  <span className="font-semibold text-slate-700">Email:</span> {r.email}
                                </div>
                              )}
                              {r.notes && (
                                <div className="text-xs text-slate-600 whitespace-pre-wrap">
                                  <span className="font-semibold text-slate-700">Notes:</span> {r.notes}
                                </div>
                              )}
                              <div>
                                <div className="text-xs font-semibold text-slate-700 mb-1.5">
                                  Documents ({(r.documents || []).length})
                                </div>
                                {(r.documents || []).length === 0 ? (
                                  <div className="text-xs text-slate-500">No documents uploaded.</div>
                                ) : (
                                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {r.documents.map((d, i) => (
                                      <li key={i}
                                        data-testid={`subsidy-doc-${r.id}-${i}`}
                                        className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                                          <span className="truncate text-slate-700">{d.original_filename}</span>
                                          <span className="text-slate-400 shrink-0">
                                            {((d.size || 0) / 1024).toFixed(0)} KB
                                          </span>
                                        </div>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); downloadFile(d.storage_path, d.original_filename); }}
                                          className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 font-semibold">
                                          <Download className="w-3.5 h-3.5" /> Download
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
